import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface GameHUDProps {
  currentScore: number;
  bestScore: number;
}
interface AnimatedNumberProps {
  value: number;
  className?: string;
}
function AnimatedNumber({ value, className }: AnimatedNumberProps) {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (value !== prevValueRef.current) {
      setIsAnimating(true);
      const diff = value - prevValueRef.current;
      const steps = 10;
      const stepValue = diff / steps;
      let current = prevValueRef.current;
      let step = 0;
      const interval = setInterval(() => {
        step++;
        current += stepValue;
        if (step >= steps) {
          setDisplayValue(value);
          clearInterval(interval);
          setIsAnimating(false);
        } else {
          setDisplayValue(Math.floor(current));
        }
      }, 30);
      prevValueRef.current = value;
      return () => clearInterval(interval);
    }
  }, [value]);
  return (
    <motion.span
      className={className}
      animate={isAnimating ? { scale: [1, 1.1, 1] } : {}}
      transition={{ duration: 0.2 }}
    >
      {displayValue}
    </motion.span>
  );
}
export function GameHUD({ currentScore, bestScore }: GameHUDProps) {
  const [showPulse, setShowPulse] = useState(false);
  const prevScoreRef = useRef(currentScore);
  useEffect(() => {
    if (currentScore !== prevScoreRef.current) {
      setShowPulse(true);
      const timeout = setTimeout(() => setShowPulse(false), 300);
      prevScoreRef.current = currentScore;
      return () => clearTimeout(timeout);
    }
  }, [currentScore]);
  return (
    <div
      className="fixed top-0 left-0 right-0 z-40 px-4 pt-[env(safe-area-inset-top,8px)]"
      style={{ touchAction: 'none' }}
    >
      <div className="flex items-center justify-between gap-4 max-w-lg mx-auto">
        <AnimatePresence>
          {showPulse && (
            <motion.div
              key="pulse"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: [0, 0.3, 0], scale: [1, 1.15, 1] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 bg-[#FFD93D] rounded-2xl"
              style={{ pointerEvents: 'none' }}
            />
          )}
        </AnimatePresence>
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-[3px_3px_0_0_#E5DDD0] px-4 py-3 flex-1 relative overflow-hidden">
          <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">
            Score
          </p>
          <p className="text-2xl font-extrabold text-[#1a1a1a] tabular-nums">
            <AnimatedNumber value={currentScore} />
          </p>
        </div>
        <div
          className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-[3px_3px_0_0_#E5DDD0] px-4 py-3 flex-1 relative overflow-hidden ${
            currentScore >= bestScore && currentScore > 0
              ? 'ring-2 ring-[#FFD93D] ring-offset-2'
              : ''
          }`}
        >
          <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">
            Best
          </p>
          <p className="text-2xl font-extrabold text-[#FFD93D] tabular-nums">
            <AnimatedNumber value={bestScore} />
          </p>
          {currentScore >= bestScore && currentScore > 0 && (
            <motion.div
              className="absolute inset-0 bg-[#FFD93D]/10 rounded-2xl"
              animate={{ opacity: [0.1, 0.2, 0.1] }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{ pointerEvents: 'none' }}
            />
          )}
        </div>
      </div>
    </div>
  );
}